import { Bounds, Point, sanitizeBounds } from "portal/utils/geo";
import { FilterData } from "portal/utils/map";
import { getClass } from "portal/utils/robots";
import { getRobotPath } from "portal/utils/routing";
import { IconButton, Tooltip } from "@mui/material";
import { isUndefined } from "portal/utils/identity";
import { Link } from "react-router-dom";
import { Marker } from "react-map-gl";
import { RobotSummaryResponse } from "protos/portal/robots";
import { RobotTypeIcon } from "../RobotTypeIcon";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import { valueOrLoading } from "portal/utils/strings";
import CarbonLogo from "portal/images/logo_icon_border.svg?react";
import React, { ReactNode, useMemo } from "react";

export const useMapRobots = (
  robots: RobotSummaryResponse[] | undefined,
  mapFilters: FilterData<boolean>,
  focusSerial?: string, // if defined, signifies that we want to focus on a particular robot
  onRobotClick?: (serial: string) => void
): {
  markers: ReactNode[]; // all robot markers regardless of focus
  focus?: Point; // either the center of all the data or the particular focused robot
  bounds?: Bounds; // if there is not a focused robot, the bounds that contains all the data
} => {
  const { isInternal } = useSelf();
  const { t } = useTranslation();

  const focus = useMemo<Point | undefined>(() => {
    if (!robots || robots.length === 0) {
      return;
    }
    const focusedRobot = isUndefined(focusSerial)
      ? undefined
      : robots.find(
          (summary) =>
            summary.robot?.serial &&
            summary.robot.serial === focusSerial &&
            getRobotPoint(summary)
        );

    const firstRobotWithLocation = robots.find((summary) =>
      getRobotPoint(summary)
    );
    const centerOnRobot = focusedRobot ?? firstRobotWithLocation;
    if (!centerOnRobot) {
      return;
    }

    return getRobotPoint(centerOnRobot);
  }, [focusSerial, robots]);

  const [markers, bounds] = useMemo<[ReactNode[], Bounds | undefined]>(() => {
    let hasBounds = false;
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    const newMarkers: ReactNode[] = [];
    for (const summary of robots ?? []) {
      if (!mapFilters.robots[getClass(summary.robot)]) {
        continue;
      }
      const point = getRobotPoint(summary);
      if (!point) {
        continue;
      }
      const [longitude, latitude] = point;
      if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
        continue;
      }

      hasBounds = !focusSerial;
      minX = Math.min(minX, latitude);
      maxX = Math.max(maxX, latitude);
      minY = Math.min(minY, longitude);
      maxY = Math.max(maxY, longitude);
      const markerIcon = isInternal ? (
        <RobotTypeIcon robotType={getClass(summary.robot)} />
      ) : (
        <CarbonLogo className="w-4 h-4 text-orange-500" />
      );
      const internalSerial = summary.robot?.serial;
      const customerSerial = summary.robot?.customerSerial;
      newMarkers.push(
        <Marker
          style={{ cursor: "pointer" }}
          key={summary.robot?.db?.id ?? internalSerial}
          latitude={latitude}
          longitude={longitude}
        >
          <Tooltip title={valueOrLoading(customerSerial, t)} arrow>
            {/* this wrapper prevents react from trying to ref an SVG which breaks*/}
            {onRobotClick && internalSerial ? (
              <IconButton
                onClick={() => {
                  onRobotClick(internalSerial);
                }}
              >
                {markerIcon}
              </IconButton>
            ) : (
              <Link to={getRobotPath(summary.robot?.serial)}>{markerIcon}</Link>
            )}
          </Tooltip>
        </Marker>
      );
    }
    const newBounds =
      hasBounds && minX !== maxX && minY !== maxY
        ? sanitizeBounds({ minX, maxX, minY, maxY })
        : undefined;
    return [newMarkers, newBounds];
  }, [focusSerial, isInternal, mapFilters.robots, onRobotClick, robots, t]);

  return { markers, focus, bounds };
};

const getRobotPoint = (summary: RobotSummaryResponse): Point | undefined => {
  const lat = summary.robot?.health?.location?.x;
  const lon = summary.robot?.health?.location?.y;
  return isUndefined(lat) || isUndefined(lon) ? undefined : [lon, lat];
};
